import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { automationsAPI, executionsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import {
  ArrowLeft,
  Play,
  Pause,
  Edit,
  Trash2,
  Calendar,
  BarChart3,
  Clock,
  Timer,
  Hash,
  CalendarPlus,
  RefreshCw,
} from 'lucide-react';
import {
  formatDate,
  formatRelativeTime,
  formatPercentage,
  parseCron,
  getStatusColor,
  formatTimeSaved,
} from '@/shared/lib/utils';
import { useAutomationModals } from '@/shared/hooks/useAutomationModals';
import type { AutomationWithStats, Execution } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Breadcrumbs } from '@travellegacy/nexops-design-system';
import { AutomationTypeBadge } from '../components/AutomationTypeBadge';

export default function AutomationDetail() {
  const { id } = useParams<{ id: string }>();

  // Use shared modals hook
  const {
    modals,
    openExecuteModal,
    openToggleModal,
    openDeleteModal,
    openEditModal,
    isExecuting,
    isToggling,
    isDeleting,
  } = useAutomationModals();

  // Fetch automation
  const { data: automation, isLoading } = useQuery({
    queryKey: ['automation', id],
    queryFn: async () => {
      const response = await automationsAPI.getById(id!);
      return response.data.data as AutomationWithStats;
    },
    enabled: !!id,
  });

  // Fetch executions
  const { data: executions } = useQuery({
    queryKey: ['executions', 'automation', id],
    queryFn: async () => {
      const response = await executionsAPI.list({
        automationId: id!,
        limit: 10,
      });
      return response.data.data as Execution[];
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Breadcrumbs Skeleton */}
        <div className="h-5 w-64 bg-muted animate-pulse rounded" />

        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-muted animate-pulse rounded" />
            <div className="flex-1 space-y-3">
              <div className="flex gap-2">
                <div className="h-6 w-24 bg-muted animate-pulse rounded-full" />
                <div className="h-6 w-16 bg-muted animate-pulse rounded-full" />
              </div>
              <div className="h-8 w-80 bg-muted animate-pulse rounded" />
              <div className="h-5 w-96 bg-muted animate-pulse rounded" />
            </div>
          </div>

          {/* Actions Skeleton */}
          <div className="flex flex-wrap gap-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 w-28 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>

        {/* Info Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-card border rounded-lg p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-8 w-16 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
                <div className="h-8 w-8 bg-muted rounded-full" />
              </div>
            </div>
          ))}
        </div>

        {/* Details Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-card border rounded-lg p-6 animate-pulse">
              <div className="h-6 w-32 bg-muted rounded mb-4" />
              <div className="space-y-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-4 w-24 bg-muted rounded" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Recent Executions Skeleton */}
        <div className="bg-card border rounded-lg p-6 animate-pulse">
          <div className="h-6 w-48 bg-muted rounded mb-4" />
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-20 bg-muted rounded-full" />
                    <div className="h-4 w-32 bg-muted rounded" />
                  </div>
                  <div className="h-4 w-16 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!automation) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/automations">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Automação não encontrada</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[{ label: 'Automações', href: '/automations' }, { label: automation.name }]}
      />

      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <Link to="/automations">
            <Button variant="ghost" size="icon" className="mt-1">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <AutomationTypeBadge type={automation.type} size="md" />
              <Badge variant={automation.isActive ? 'success' : 'error'}>
                {automation.isActive ? 'Ativa' : 'Inativa'}
              </Badge>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground break-words">
              {automation.name}
            </h1>
            {automation.description && (
              <p className="text-sm sm:text-base text-muted-foreground mt-2 break-words">
                {automation.description}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => openExecuteModal(automation)}
            disabled={isExecuting}
            className="flex-1 sm:flex-none"
          >
            <Play className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Executar</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => openToggleModal(automation)}
            disabled={isToggling}
            className="flex-1 sm:flex-none"
          >
            {automation.isActive ? (
              <>
                <Pause className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Desativar</span>
              </>
            ) : (
              <>
                <Play className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Ativar</span>
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => openEditModal(automation)}
            className="flex-1 sm:flex-none"
          >
            <Edit className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Editar</span>
          </Button>
          <Button
            variant="destructive"
            onClick={() => openDeleteModal(automation, true)}
            disabled={isDeleting}
            className="flex-1 sm:flex-none"
          >
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Deletar</span>
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {automation.stats.totalExecutions}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Total Execuções</p>
              </div>
              <BarChart3 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-500">
                  {automation.stats.successfulExecutions}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Sucesso</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-600 dark:text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-500">
                  {automation.stats.failedExecutions}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Erros</p>
              </div>
              <BarChart3 className="h-8 w-8 text-red-600 dark:text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div
                  className={
                    automation.stats.successRate >= 90
                      ? 'text-2xl font-bold text-green-600 dark:text-green-500'
                      : automation.stats.successRate >= 70
                        ? 'text-2xl font-bold text-yellow-600 dark:text-yellow-500'
                        : 'text-2xl font-bold text-red-600 dark:text-red-500'
                  }
                >
                  {formatPercentage(automation.stats.successRate)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Taxa de Sucesso</p>
              </div>
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatTimeSaved(automation.stats.totalTimeSavedMinutes)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Tempo Economizado</p>
              </div>
              <Timer className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Config */}
        <Card>
          <CardHeader>
            <CardTitle>Configuração</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground flex items-center">
                <Hash className="h-4 w-4 mr-1" />
                ID
              </p>
              <p className="text-sm text-muted-foreground font-mono">{automation.id}</p>
            </div>

            {automation.schedule && (
              <div>
                <p className="text-sm font-medium text-foreground flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Agendamento
                </p>
                <p className="text-sm text-muted-foreground font-mono">
                  {parseCron(automation.schedule)}
                </p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-foreground flex items-center">
                <CalendarPlus className="h-4 w-4 mr-1" />
                Criado em
              </p>
              <p className="text-sm text-muted-foreground">{formatDate(automation.createdAt)}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground flex items-center">
                <RefreshCw className="h-4 w-4 mr-1" />
                Atualizado em
              </p>
              <p className="text-sm text-muted-foreground">{formatDate(automation.updatedAt)}</p>
            </div>

            {automation.stats.lastExecution && (
              <div>
                <p className="text-sm font-medium text-foreground flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Última Execução
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatRelativeTime(automation.stats.lastExecution.startedAt)}
                </p>
                <Badge className={`mt-1 ${getStatusColor(automation.stats.lastExecution.status)}`}>
                  {automation.stats.lastExecution.status}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Config JSON */}
        <Card>
          <CardHeader>
            <CardTitle>Config (JSON)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded text-xs overflow-x-auto text-foreground max-w-full whitespace-pre-wrap break-all">
              {JSON.stringify(automation.config, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Execuções Recentes</span>
            <Link to={`/executions?automationId=${id}`}>
              <Button variant="ghost" size="sm">
                Ver todas →
              </Button>
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!executions || executions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma execução encontrada
            </div>
          ) : (
            <div className="space-y-2">
              {executions.map((execution) => (
                <Link
                  key={execution.id}
                  to={`/executions/${execution.id}`}
                  className="block border rounded-lg p-4 hover:bg-accent transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Badge className={getStatusColor(execution.status)}>{execution.status}</Badge>
                      <span className="text-sm text-foreground">
                        {formatRelativeTime(execution.startedAt)}
                      </span>
                    </div>
                    {execution.duration && (
                      <span className="text-sm text-muted-foreground">
                        {(execution.duration / 1000).toFixed(1)}s
                      </span>
                    )}
                  </div>
                  {execution.error && (
                    <p className="text-sm text-destructive mt-2 truncate">{execution.error}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit, Execute, Toggle, Delete Modals from hook */}
      {modals}
    </div>
  );
}
